import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
})
export class InputTextComponent implements OnInit, AfterViewInit {
  @ViewChild('bareInput') bareInput?: ElementRef<HTMLInputElement>;
  @Input() inputId: string;
  @Input() label: string;
  @Input() isInvalid = false;
  @Input() isDisabled = false;
  @Input() required = false;
  @Input() optionalTag = true;
  @Input() autocomplete = 'off';
  @Input() control: any;
  @Input() maxLength: number = 524288; // HTML5 max length
  @Input() trim = 'blur';
  @Input() errorText: string;
  @Input() inputType: string = 'text';
  @Input() placeholder: string = '';
  /** Optional inputmode attribute (e.g. 'numeric' for digit-only fields). */
  @Input() inputMode: string | null = null;
  /** Native min/max attributes (number spinners); also fed to range messages. */
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;
  /**
   * Standalone (no-FormControl) bare usage: drive the value from the parent
   * (`[value]` + inputEvent/keyupEvent), mirroring the checkbox/toggle
   * standalone convention. Ignored when [control] is set.
   */
  @Input() value: string | number | null = null;
  /** Focus the input after view init (dialogs' cdkFocusInitial/autofocus). */
  @Input() autoFocus = false;
  /** Chrome-less variant: drop the .form-control box (in-widget inputs such
   *  as the tags-input chip field own their own container styling). */
  @Input() plain = false;
  /**
   * Admin mode: render just the `.form-control` (standard admin styling, no
   * floating label) so it can sit inside an <app-form-row> that supplies the
   * static label + grid. Default stays floating for the customer UI.
   */
  @Input() bare = false;
  @Input() readonly = false;
  /** Render a <textarea> instead of <input> (bare mode). */
  @Input() multiline = false;
  @Input() rows = 3;
  /** Extra class(es) on the control, e.g. 'percentage-field' or 'read-only'. */
  @Input() cssClass = '';
  /**
   * Optional per-error message overrides for the auto-rendered validation
   * (bare mode). Keys: required | minlength | maxlength | email | pattern | …
   * When omitted, sensible admin defaults (admin.global.field.*) are used.
   * An explicit `errorText` still wins over this.
   */
  @Input() customValidationMessages: { [key: string]: string } = {};
  /** Optional data-testid applied to the input/textarea itself (bare mode). */
  @Input() testId: string | null = null;
  /**
   * Hint rendered under the control as the app's `.explanation` (bare mode).
   * Pass a translate key; for parameterized hints pre-translate at the call
   * site (`[helpText]="'key' | translate: {...}"` — a non-key string passes
   * through the pipe unchanged).
   */
  @Input() helpText = '';
  // Optional data-testid applied to the validation-error container so automation
  // can target a specific field's error without relying on generic structural
  // classes. Defaults to null -> attribute is not rendered (no behavior change).
  @Input() errorTestId: string | null = null;
  @Output() keyupEvent = new EventEmitter<KeyboardEvent>();
  /** Raw focus event (call sites validating/selecting on focus). */
  @Output() focusEvent = new EventEmitter<FocusEvent>();
  /** Raw keydown (e.g. to stopPropagation inside a mat-select panel). */
  @Output() keydownEvent = new EventEmitter<KeyboardEvent>();
  @Output() changeEvent = new EventEmitter<Event>();
  @Output() blurEvent = new EventEmitter<Event>();
  @Output() focusoutEvent = new EventEmitter<Event>();
  @Output() inputEvent = new EventEmitter<Event>();

  ngOnInit() {
    if (!this.inputId) {
      this.inputId = this.generateRandomId();
    }
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      this.bareInput?.nativeElement.focus();
    }
  }

  generateRandomId(): string {
    return 'id-' + Math.random().toString(36).substring(2, 11);
  }

  get hasValue(): boolean {
    const value = this.control?.value;
    return value != null && String(value).trim().length > 0;
  }

  /** True when the field should show an error (explicit isInvalid or a touched invalid control). */
  get showError(): boolean {
    return (
      this.isInvalid ||
      !!(
        this.control?.invalid &&
        (this.control?.dirty || this.control?.touched)
      )
    );
  }

  /** Translation key for the current validation error (bare mode auto-render). */
  get errorMessageKey(): string {
    const errors = this.control?.errors;
    if (!errors) {
      return '';
    }
    const custom = this.customValidationMessages || {};
    if (errors['required'] || errors['whitespace']) {
      return custom['required'] || 'admin.global.field.required';
    }
    if (errors['minlength']) {
      return custom['minlength'] || 'admin.global.field.minLength';
    }
    if (errors['maxlength']) {
      return custom['maxlength'] || 'admin.global.field.maxLength';
    }
    const firstKey = Object.keys(errors)[0];
    return custom[firstKey] || 'admin.global.field.validation';
  }

  /** Interpolation params for the current validation error (e.g. min/max length). */
  get errorMessageParams(): { [key: string]: any } {
    const errors = this.control?.errors;
    if (!errors) {
      return {};
    }
    if (errors['minlength']) {
      return { minLength: errors['minlength'].requiredLength };
    }
    if (errors['maxlength']) {
      return { maxLength: errors['maxlength'].requiredLength };
    }
    if (errors['min']) {
      return { minLength: errors['min'].min, ...this.rangeParams };
    }
    if (errors['max']) {
      return { maxLength: errors['max'].max, ...this.rangeParams };
    }
    return {};
  }

  /** {min,max} from the component inputs, for range-style messages
   *  (e.g. admin.global.field.rangeValue interpolating both bounds). */
  private get rangeParams(): { [key: string]: any } {
    return {
      ...(this.min != null && { min: this.min }),
      ...(this.max != null && { max: this.max }),
    };
  }

  onKeyup(event: KeyboardEvent) {
    this.keyupEvent.emit(event);
  }

  onInput(event: Event) {
    this.inputEvent.emit(event);
  }

  onChange(event: Event) {
    this.changeEvent.emit(event);
  }

  onBlur(event: Event) {
    this.blurEvent.emit(event);
  }

  onFocusout(event: Event) {
    this.focusoutEvent.emit(event);
  }
}
